import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Logo } from "@/components/logo";
import { Check, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { seoMeta } from "@/lib/seo/head";

export const Route = createFileRoute("/onboarding")({
  head: () =>
    seoMeta({
      title: "Onboarding",
      description: "Configura il tuo account LexAIPro: aree di pratica, preferenze e impostazioni iniziali per iniziare subito a lavorare.",
      path: "/onboarding",
      noindex: true,
    }),
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: "/login" });
  },
  component: OnboardingPage,
});

const AREA_KEYS = ["civil","commercial","labor","criminal","tax","family","admin","immigration","ip","privacy"] as const;

function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    first_name: "", last_name: "",
    firm_name: "", bar_order: "", bar_number: "",
    specializations: [] as string[],
  });

  const STEPS = useMemo(() => [
    t("onboarding.steps.profile"),
    t("onboarding.steps.firm"),
    t("onboarding.steps.areas"),
    t("onboarding.steps.ready"),
  ], [t]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const toggleArea = (a: string) => {
    setData((d) => ({
      ...d,
      specializations: d.specializations.includes(a)
        ? d.specializations.filter((x) => x !== a)
        : [...d.specializations, a],
    }));
  };

  const finish = async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const { error } = await supabase.from("profiles").update({ ...data, onboarded: true }).eq("id", u.user.id);
    if (error) return toast.error(error.message);
    toast.success(t("onboarding.welcome"));
    navigate({ to: "/app" });
  };

  return (
    <div className="min-h-screen bg-background px-4 py-12">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 flex justify-center"><Logo variant="light" size={48} /></div>

        {/* Stepper */}
        <div className="mb-8 flex items-center justify-between">
          {STEPS.map((label, i) => (
            <div key={label} className="flex flex-1 items-center">
              <div className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-colors",
                i <= step ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"
              )}>
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={cn("mx-2 h-0.5 flex-1", i < step ? "bg-accent" : "bg-muted")} />
              )}
            </div>
          ))}
        </div>

        <Card className="p-8">
          {step === 0 && (
            <>
              <h2 className="font-display text-2xl font-bold">{t("onboarding.step0Title")}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.step0Desc")}</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div><Label>{t("onboarding.fName")}</Label><Input value={data.first_name} onChange={(e) => setData({ ...data, first_name: e.target.value })} /></div>
                <div><Label>{t("onboarding.fLastName")}</Label><Input value={data.last_name} onChange={(e) => setData({ ...data, last_name: e.target.value })} /></div>
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <h2 className="font-display text-2xl font-bold">{t("onboarding.step1Title")}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.step1Desc")}</p>
              <div className="mt-6 space-y-4">
                <div><Label>{t("onboarding.fFirm")}</Label><Input value={data.firm_name} onChange={(e) => setData({ ...data, firm_name: e.target.value })} /></div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label>{t("onboarding.fBarOrder")}</Label><Input placeholder={t("onboarding.fBarOrderPh")} value={data.bar_order} onChange={(e) => setData({ ...data, bar_order: e.target.value })} /></div>
                  <div><Label>{t("onboarding.fBarNumber")}</Label><Input value={data.bar_number} onChange={(e) => setData({ ...data, bar_number: e.target.value })} /></div>
                </div>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <h2 className="font-display text-2xl font-bold">{t("onboarding.step2Title")}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.step2Desc")}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {AREA_KEYS.map((k) => {
                  const label = t(`onboarding.areas.${k}`);
                  return (
                    <button
                      key={k}
                      onClick={() => toggleArea(label)}
                      className={cn(
                        "rounded-full border px-4 py-2 text-sm font-medium transition-all",
                        data.specializations.includes(label)
                          ? "border-accent bg-accent text-accent-foreground"
                          : "border-border bg-background text-muted-foreground hover:border-accent"
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {step === 3 && (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent text-accent-foreground">
                <Sparkles className="h-8 w-8" />
              </div>
              <h2 className="font-display text-2xl font-bold">{t("onboarding.step3Title", { name: data.first_name || t("onboarding.step3Default") })}</h2>
              <p className="mt-2 text-muted-foreground">
                {t("onboarding.step3Desc")}
              </p>
            </div>
          )}

          <div className="mt-8 flex justify-between">
            <Button variant="ghost" onClick={prev} disabled={step === 0}>
              <ArrowLeft className="mr-2 h-4 w-4" /> {t("onboarding.back")}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next}>
                {t("onboarding.next")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={finish} className="bg-accent text-accent-foreground hover:bg-accent/90">
                {t("onboarding.enter")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
